/**
 * Snapshots: taking one off a `World`, putting one back on, and the delta /
 * merge pair that lets a snapshot describe only what changed.
 *
 * ===========================================================================
 * A SNAPSHOT IS NOT A SIM STATE
 * ===========================================================================
 *
 * `serializeState` (../sim/state) is the whole world: every timer, the jump
 * button's previous level, the powerslide ramp, the RNG. A `Snapshot` is a
 * deliberately smaller thing — the fields a remote client needs to DRAW the
 * game and to CORRECT its own prediction, quantized down to a couple of hundred
 * bytes. It is a projection, and it is lossy on purpose.
 *
 * The consequence is the one thing to get right about `applySnapshot`: it
 * overwrites the authoritative fields of whatever world it is handed and leaves
 * everything else alone. A client that rewinds must therefore restore its OWN
 * serialized state for that tick first and apply the snapshot on top — the
 * snapshot supplies the authority, the stored state supplies the hidden
 * machinery the authority does not carry. `PredictionClient` in ./prediction
 * does exactly that, and doing it the other way round (apply the snapshot to
 * the present world and replay) silently loses the dodge timer and the boost
 * burst, which is a divergence that only shows up when a player flips.
 *
 * ===========================================================================
 * WHAT DELTAS ARE FOR
 * ===========================================================================
 *
 * `deltaSnapshot(current, base)` compares the two AT WIRE PRECISION — it
 * quantizes both sides and diffs the integers — so a field is only "changed"
 * when the receiver would actually decode a different number. Diffing the
 * floats instead would mark every field of every car changed on every tick and
 * the delta would never be smaller than the keyframe.
 *
 * Fields the delta does not carry are zeroed rather than left holding the
 * current value, so a delta round-trips through the codec exactly: what the
 * mask does not name, nobody may read.
 *
 * A delta is useless without its base, and under an unreliable transport the
 * base is routinely gone. `SnapshotHistory.resolve` returns null in that case
 * and the caller DROPS THE FRAME. It does not guess, does not apply the delta
 * to the wrong base, and does not ask for a retransmit — the next keyframe
 * repairs everything, which is the property that makes swapping WebSocket for
 * an unreliable DataChannel a configuration change rather than a rewrite.
 */

import type { MatchPhase } from '../game/match';
import type { CarState } from '../physics/car-state';
import type { Mat3 } from '../physics/math';
import type { World } from '../sim/world';
import {
  ANGULAR_RANGE,
  BALL_ALL,
  BALL_ANGULAR_X,
  BALL_POSITION_X,
  BALL_VELOCITY_X,
  BOOST_RANGE,
  CAR_ALL,
  CAR_ANGULAR_X,
  CAR_BOOST,
  CAR_FLAGS,
  CAR_ORIENTATION,
  CAR_POSITION_X,
  CAR_VELOCITY_X,
  FLAG_DEMOLISHED,
  FLAG_HAS_FLIP,
  FLAG_IS_DODGING,
  FLAG_JUMPED,
  FLAG_ON_GROUND,
  FLAG_SUPERSONIC,
  PAD_TIMER_RANGE,
  POSITION_RANGE,
  VELOCITY_RANGE,
  asDeltaSnapshot,
  matrixToQuat,
  packQuat,
  quantize16,
  quantizeU8,
  quantizeU16,
  quatToMatrix,
  type DeltaCarSnapshot,
  type DeltaSnapshot,
} from './codec';
import type { BodySnapshot, CarSnapshot, Snapshot } from './protocol';

// ---------------------------------------------------------------------------
// Match phase <-> byte
// ---------------------------------------------------------------------------

/**
 * Wire order of `MatchPhase`. APPEND ONLY: the index is the byte, so reordering
 * this array turns every stored replay and every mid-flight snapshot into a
 * different match.
 */
export const MATCH_PHASES: readonly MatchPhase[] = [
  'countdown',
  'playing',
  'goal',
  'replay',
  'overtime-pending',
  'overtime',
  'ended',
];

export const phaseToWire = (phase: MatchPhase): number => {
  const i = MATCH_PHASES.indexOf(phase);
  if (i < 0) throw new Error(`match phase ${phase} has no wire code`);
  return i;
};

export const phaseFromWire = (code: number): MatchPhase => MATCH_PHASES[code] ?? 'playing';

// ---------------------------------------------------------------------------
// Car flags
// ---------------------------------------------------------------------------

/** The six booleans protocol.ts packs into `CarSnapshot.flags`. */
export const packCarFlags = (car: CarState): number =>
  (car.onGround ? FLAG_ON_GROUND : 0) |
  (car.supersonic ? FLAG_SUPERSONIC : 0) |
  (car.demolished ? FLAG_DEMOLISHED : 0) |
  (car.hasFlip ? FLAG_HAS_FLIP : 0) |
  (car.isDodging ? FLAG_IS_DODGING : 0) |
  (car.hasJumped ? FLAG_JUMPED : 0);

const applyCarFlags = (car: CarState, flags: number): void => {
  car.onGround = (flags & FLAG_ON_GROUND) !== 0;
  car.supersonic = (flags & FLAG_SUPERSONIC) !== 0;
  car.demolished = (flags & FLAG_DEMOLISHED) !== 0;
  car.hasFlip = (flags & FLAG_HAS_FLIP) !== 0;
  car.isDodging = (flags & FLAG_IS_DODGING) !== 0;
  car.hasJumped = (flags & FLAG_JUMPED) !== 0;
};

// ---------------------------------------------------------------------------
// capture
// ---------------------------------------------------------------------------

const triple = (v: { x: number; y: number; z: number }): [number, number, number] => [v.x, v.y, v.z];

/**
 * A COMPLETE snapshot of `world`.
 *
 * `baseTick` is carried through as a label, not as an instruction: this
 * function always reads every car, the ball and all 34 pads, because it is the
 * only thing that can. Trimming happens in `deltaSnapshot`, which is the only
 * function that has both sides of the comparison. The server's shape is
 *
 *     const full = captureSnapshot(world, ack, -1);
 *     const out  = base ? deltaSnapshot(full, base) : full;
 *
 * and `baseTick` on this call exists so a caller that already knows it is
 * building a delta does not have to patch the field afterwards.
 */
export const captureSnapshot = (world: World, ackedInputSeq: number, baseTick: number): Snapshot => ({
  tick: world.tick,
  baseTick,
  ackedInputSeq,
  cars: world.cars.map(
    (car): CarSnapshot => ({
      position: triple(car.position),
      velocity: triple(car.velocity),
      angularVelocity: triple(car.angularVelocity),
      orientation: matrixToQuat(car.orientation),
      boost: car.boostAmount,
      flags: packCarFlags(car),
    })
  ),
  ball: {
    position: triple(world.ball.position),
    velocity: triple(world.ball.velocity),
    angularVelocity: triple(world.ball.angularVelocity),
  },
  padChanges: world.boostPads.pads.map((pad) => ({
    index: pad.index,
    active: pad.active,
    respawnTimer: pad.respawnTimer,
  })),
  match: {
    phase: phaseToWire(world.match.phase),
    clock: world.match.clock,
    score: [world.match.score[0], world.match.score[1]],
  },
});

// ---------------------------------------------------------------------------
// apply
// ---------------------------------------------------------------------------

/**
 * Overwrite the authoritative fields of `world` from a COMPLETE snapshot.
 *
 * Refuses a delta outright. Merging is `mergeSnapshot`'s job and it needs a
 * base this function does not have; applying a delta here would write zeroes
 * into every field the mask left out, which reads as "the ball is at the origin
 * and every car is upright at the corner of the map".
 *
 * Everything the snapshot does not carry — jump and dodge timers, the boost
 * burst, the powerslide ramp, the RNG, the bump cooldowns — is LEFT AS IT IS.
 * See the header.
 */
export const applySnapshot = (world: World, snap: Snapshot): void => {
  if (snap.baseTick >= 0) {
    throw new Error(
      `applySnapshot needs a complete snapshot; got a delta against tick ${snap.baseTick}`
    );
  }

  world.tick = snap.tick;

  const n = Math.min(world.cars.length, snap.cars.length);
  for (let i = 0; i < n; i++) {
    const car = world.cars[i];
    const s = snap.cars[i];
    car.position = { x: s.position[0], y: s.position[1], z: s.position[2] };
    car.velocity = { x: s.velocity[0], y: s.velocity[1], z: s.velocity[2] };
    car.angularVelocity = {
      x: s.angularVelocity[0],
      y: s.angularVelocity[1],
      z: s.angularVelocity[2],
    };
    car.orientation = quatToMatrix(s.orientation);
    car.boostAmount = s.boost;
    applyCarFlags(car, s.flags);
  }

  world.ball.position = {
    x: snap.ball.position[0],
    y: snap.ball.position[1],
    z: snap.ball.position[2],
  };
  world.ball.velocity = {
    x: snap.ball.velocity[0],
    y: snap.ball.velocity[1],
    z: snap.ball.velocity[2],
  };
  world.ball.angularVelocity = {
    x: snap.ball.angularVelocity[0],
    y: snap.ball.angularVelocity[1],
    z: snap.ball.angularVelocity[2],
  };

  for (const change of snap.padChanges) {
    const pad = world.boostPads.pads[change.index];
    if (pad === undefined) continue;
    pad.active = change.active;
    pad.respawnTimer = change.respawnTimer;
  }

  world.match.phase = phaseFromWire(snap.match.phase);
  world.match.clock = snap.match.clock;
  world.match.score = [snap.match.score[0], snap.match.score[1]];
};

// ---------------------------------------------------------------------------
// delta / merge
// ---------------------------------------------------------------------------

/** What a delta writes for an orientation it is not carrying. Never read by the
 *  receiver — the mask says it is absent — but it has to be SOMETHING, and a
 *  zero quaternion would decode into a degenerate matrix if anyone ever did. */
const IDENTITY: readonly [number, number, number, number] = [0, 0, 0, 1];

/** Which axes of a vector quantize differently. `bits` is the low bit of the
 *  three-bit group in the mask. */
const vecMask = (
  a: readonly [number, number, number],
  b: readonly [number, number, number],
  range: number,
  bits: number
): number =>
  (quantize16(a[0], range) !== quantize16(b[0], range) ? bits : 0) |
  (quantize16(a[1], range) !== quantize16(b[1], range) ? bits << 1 : 0) |
  (quantize16(a[2], range) !== quantize16(b[2], range) ? bits << 2 : 0);

const pick = (
  v: readonly [number, number, number],
  mask: number,
  bits: number
): [number, number, number] => [
  mask & bits ? v[0] : 0,
  mask & (bits << 1) ? v[1] : 0,
  mask & (bits << 2) ? v[2] : 0,
];

const carDelta = (current: CarSnapshot, base: CarSnapshot): DeltaCarSnapshot => {
  const mask =
    vecMask(current.position, base.position, POSITION_RANGE, CAR_POSITION_X) |
    vecMask(current.velocity, base.velocity, VELOCITY_RANGE, CAR_VELOCITY_X) |
    vecMask(current.angularVelocity, base.angularVelocity, ANGULAR_RANGE, CAR_ANGULAR_X) |
    (packQuat(current.orientation) !== packQuat(base.orientation) ? CAR_ORIENTATION : 0) |
    (quantizeU8(current.boost, BOOST_RANGE) !== quantizeU8(base.boost, BOOST_RANGE)
      ? CAR_BOOST
      : 0) |
    (current.flags !== base.flags ? CAR_FLAGS : 0);

  return {
    position: pick(current.position, mask, CAR_POSITION_X),
    velocity: pick(current.velocity, mask, CAR_VELOCITY_X),
    angularVelocity: pick(current.angularVelocity, mask, CAR_ANGULAR_X),
    orientation: mask & CAR_ORIENTATION ? current.orientation : IDENTITY,
    boost: mask & CAR_BOOST ? current.boost : 0,
    flags: mask & CAR_FLAGS ? current.flags : 0,
    changed: mask,
  };
};

/**
 * `current` expressed against `base`. `base` must be COMPLETE — a delta against
 * a delta is not a thing this protocol has, because it would make a single lost
 * frame poison an unbounded chain of successors.
 */
export const deltaSnapshot = (current: Snapshot, base: Snapshot): Snapshot => {
  if (base.baseTick >= 0) {
    throw new Error(`delta base must be a complete snapshot; tick ${base.tick} is itself a delta`);
  }

  const basePads = new Map(base.padChanges.map((p) => [p.index, p]));
  const padChanges = current.padChanges.filter((pad) => {
    const was = basePads.get(pad.index);
    if (was === undefined) return true;
    return (
      was.active !== pad.active ||
      quantizeU16(was.respawnTimer, PAD_TIMER_RANGE) !==
        quantizeU16(pad.respawnTimer, PAD_TIMER_RANGE)
    );
  });

  const ballChanged =
    vecMask(current.ball.position, base.ball.position, POSITION_RANGE, BALL_POSITION_X) |
    vecMask(current.ball.velocity, base.ball.velocity, VELOCITY_RANGE, BALL_VELOCITY_X) |
    vecMask(
      current.ball.angularVelocity,
      base.ball.angularVelocity,
      ANGULAR_RANGE,
      BALL_ANGULAR_X
    );

  const out: DeltaSnapshot = {
    tick: current.tick,
    baseTick: base.tick,
    ackedInputSeq: current.ackedInputSeq,
    cars: current.cars.map((car, i) => {
      const from = base.cars[i];
      // A car that did not exist in the base (a late joiner) has nothing to
      // delta against and travels whole.
      return from === undefined ? { ...car, changed: CAR_ALL } : carDelta(car, from);
    }),
    ball: {
      position: pick(current.ball.position, ballChanged, BALL_POSITION_X),
      velocity: pick(current.ball.velocity, ballChanged, BALL_VELOCITY_X),
      angularVelocity: pick(current.ball.angularVelocity, ballChanged, BALL_ANGULAR_X),
    },
    padChanges,
    match: {
      phase: current.match.phase,
      clock: current.match.clock,
      score: [current.match.score[0], current.match.score[1]],
    },
    ballChanged,
  };
  return out;
};

const mergeVec = (
  delta: readonly [number, number, number],
  base: readonly [number, number, number],
  mask: number,
  bits: number
): [number, number, number] => [
  mask & bits ? delta[0] : base[0],
  mask & (bits << 1) ? delta[1] : base[1],
  mask & (bits << 2) ? delta[2] : base[2],
];

/**
 * Rebuild a complete snapshot from `base` plus `delta`.
 *
 * Throws when `delta.baseTick` is not `base.tick`. That is not defensiveness
 * for its own sake: applying a delta to the wrong base produces a snapshot that
 * is entirely plausible and entirely wrong, and the symptom is a car that
 * stutters between two positions for as long as the mistake persists. Callers
 * on the receive path should be going through `SnapshotHistory.resolve`, which
 * turns the same condition into a dropped frame.
 *
 * The result is a KEYFRAME (`baseTick === -1`) with no masks, so it can be fed
 * straight to `applySnapshot` and can serve as the base for the next delta.
 */
export const mergeSnapshot = (base: Snapshot, delta: Snapshot): Snapshot => {
  if (delta.baseTick < 0) {
    // Already complete. Copy so the caller cannot alias the history.
    return cloneSnapshot(delta);
  }
  if (delta.baseTick !== base.tick) {
    throw new Error(
      `snapshot ${delta.tick} deltas against tick ${delta.baseTick}, but the base is tick ${base.tick}`
    );
  }
  if (base.baseTick >= 0) {
    throw new Error(`merge base must be complete; tick ${base.tick} is itself a delta`);
  }

  const d = asDeltaSnapshot(delta);
  const bmask = d.ballChanged ?? BALL_ALL;

  const pads = base.padChanges.map((p) => ({ ...p }));
  const byIndex = new Map(pads.map((p, i) => [p.index, i]));
  for (const change of delta.padChanges) {
    const at = byIndex.get(change.index);
    if (at === undefined) {
      byIndex.set(change.index, pads.length);
      pads.push({ ...change });
    } else {
      pads[at] = { ...change };
    }
  }

  return {
    tick: delta.tick,
    baseTick: -1,
    ackedInputSeq: delta.ackedInputSeq,
    cars: d.cars.map((car, i): CarSnapshot => {
      const from = base.cars[i];
      const mask = car.changed ?? CAR_ALL;
      if (from === undefined) {
        return {
          position: [...car.position] as [number, number, number],
          velocity: [...car.velocity] as [number, number, number],
          angularVelocity: [...car.angularVelocity] as [number, number, number],
          orientation: [...car.orientation] as [number, number, number, number],
          boost: car.boost,
          flags: car.flags,
        };
      }
      return {
        position: mergeVec(car.position, from.position, mask, CAR_POSITION_X),
        velocity: mergeVec(car.velocity, from.velocity, mask, CAR_VELOCITY_X),
        angularVelocity: mergeVec(
          car.angularVelocity,
          from.angularVelocity,
          mask,
          CAR_ANGULAR_X
        ),
        orientation:
          mask & CAR_ORIENTATION
            ? ([...car.orientation] as [number, number, number, number])
            : ([...from.orientation] as [number, number, number, number]),
        boost: mask & CAR_BOOST ? car.boost : from.boost,
        flags: mask & CAR_FLAGS ? car.flags : from.flags,
      };
    }),
    ball: {
      position: mergeVec(delta.ball.position, base.ball.position, bmask, BALL_POSITION_X),
      velocity: mergeVec(delta.ball.velocity, base.ball.velocity, bmask, BALL_VELOCITY_X),
      angularVelocity: mergeVec(
        delta.ball.angularVelocity,
        base.ball.angularVelocity,
        bmask,
        BALL_ANGULAR_X
      ),
    },
    padChanges: pads,
    match: {
      phase: delta.match.phase,
      clock: delta.match.clock,
      score: [delta.match.score[0], delta.match.score[1]],
    },
  };
};

/** A deep copy that shares nothing mutable and carries no delta masks. */
export const cloneSnapshot = (snap: Snapshot): Snapshot => ({
  tick: snap.tick,
  baseTick: snap.baseTick,
  ackedInputSeq: snap.ackedInputSeq,
  cars: snap.cars.map(
    (car): CarSnapshot => ({
      position: [...car.position] as [number, number, number],
      velocity: [...car.velocity] as [number, number, number],
      angularVelocity: [...car.angularVelocity] as [number, number, number],
      orientation: [...car.orientation] as [number, number, number, number],
      boost: car.boost,
      flags: car.flags,
    })
  ),
  ball: cloneBody(snap.ball),
  padChanges: snap.padChanges.map((p) => ({ ...p })),
  match: {
    phase: snap.match.phase,
    clock: snap.match.clock,
    score: [snap.match.score[0], snap.match.score[1]],
  },
});

const cloneBody = (b: BodySnapshot): BodySnapshot => ({
  position: [...b.position] as [number, number, number],
  velocity: [...b.velocity] as [number, number, number],
  angularVelocity: [...b.angularVelocity] as [number, number, number],
});

/** Orientation of a snapshot car as the matrix the simulation uses. */
export const snapshotOrientation = (car: CarSnapshot): Mat3 => quatToMatrix(car.orientation);

// ---------------------------------------------------------------------------
// History — the thing that makes a lost base survivable
// ---------------------------------------------------------------------------

/**
 * A small ring of COMPLETE snapshots, kept by both ends: the server remembers
 * what it has sent so it can delta against a tick the client acked, and the
 * client remembers what it has resolved so a delta can be completed.
 *
 * Capacity is in snapshots, not ticks. At the default 30 Hz snapshot rate 64
 * entries is a little over two seconds, which comfortably outlives any round
 * trip a playable match has.
 */
export class SnapshotHistory {
  private readonly ring: (Snapshot | null)[];
  private newest = -1;

  constructor(readonly capacity = 64) {
    this.ring = new Array<Snapshot | null>(capacity).fill(null);
  }

  /** Newest tick held, or -1 when empty. */
  get newestTick(): number {
    return this.newest;
  }

  /** Store a complete snapshot. Deltas are rejected — they are not a base. */
  remember(snap: Snapshot): void {
    if (snap.baseTick >= 0) throw new Error('SnapshotHistory only holds complete snapshots');
    this.ring[((snap.tick % this.capacity) + this.capacity) % this.capacity] = snap;
    if (snap.tick > this.newest) this.newest = snap.tick;
  }

  get(tick: number): Snapshot | null {
    if (tick < 0) return null;
    const slot = this.ring[((tick % this.capacity) + this.capacity) % this.capacity];
    return slot !== null && slot.tick === tick ? slot : null;
  }

  /**
   * Complete an inbound snapshot and remember it.
   *
   * Returns null when the frame cannot be completed — the base has aged out, or
   * never arrived. That is the DROP path and it is the normal case on a lossy
   * link: the caller does nothing at all and the next keyframe repairs the
   * client. Nothing is retransmitted and nothing is guessed.
   */
  resolve(snap: Snapshot): Snapshot | null {
    if (snap.baseTick < 0) {
      this.remember(snap);
      return snap;
    }
    const base = this.get(snap.baseTick);
    if (base === null) return null;
    const merged = mergeSnapshot(base, snap);
    this.remember(merged);
    return merged;
  }

  clear(): void {
    this.ring.fill(null);
    this.newest = -1;
  }
}
